import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import {
  buildListParams,
  parseFilter,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";
import { getSortObj } from "common/lib/tableDataOptions";
import { deletePayment, getPayments } from "common/services/payments.service";
import TableContainer from "../../Components/Common/TableContainer";
import AddPayment from "./AddPayment";

import BulkActionBar from "Components/Common/BulkActionBar";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import { isPaymentActive } from "common/lib/paymentOptions";
import UpdatePayment from "./UpdatePayment";
// import PositionTypeFilter from "./PositionTypeFilter";
// import AgencyFilter from "./AgencyFilter";
import BreadCrumb from "Components/Common/BreadCrumb";
import DeleteModal from "Components/Common/DeleteModal";
import { useProfile } from "Components/Hooks/UserHooks";
import { normalize } from "common/lib/normalize";
import { isAllowed } from "common/lib/permissionUtils";
import * as moment from "moment";
import { toast } from "react-toastify";
import Filters from "./Filters";
import Import from "./Import";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  positionType: string;
  agency: string;
  dateRange: DateRange;
  region: string;
  mda: string;
  paymentCategory: string;
  thematicArea: string;
  sector: string;
  status?: string;
  projectOrFund?: string;
}

// @ts-ignore
const TextWrapper: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return null;
  return <div style={{ whiteSpace: "pre-wrap", width: "220px" }}>{value}</div>;
};

const defaultSort = [{ id: "createdAt", desc: true }];

const defaultFilterValue: FilterValue = {
  positionType: "",
  agency: "",
  dateRange: {
    fromDate: null,
    toDate: null,
  },
  region: "",
  mda: "",
  paymentCategory: "",
  thematicArea: "",
  sector: "",
  status: "active",
};

const Payments = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: parsePage(searchParams),
    pageSize: parsePageSize(searchParams),
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => parseSort(searchParams.get("sort"), defaultSort));

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const fetchData = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
    setFilterValue(defaultFilterValue);
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const showAddModalForm = () => {
    setIsAddModalOpen(true);
  };

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModalForm = (data: any) => {
    setSelectedRecord(data);
    setIsUpdateModalOpen(true);
  };

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));
  
  const [filterValue, setFilterValue] = useState(() => parseFilter(searchParams, defaultFilterValue));

  const unCheckAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    if (checkall) checkall.checked = false;
    // Row checkboxes are plain uncontrolled inputs, so resetting state alone
    // leaves them visually checked; clear the DOM too (used by the floating
    // bar's "Clear selection" as well as the pre-fetch reset below).
    document.querySelectorAll<HTMLInputElement>(".tableCheckbox").forEach((ele) => {
      ele.checked = false;
    });
    setSelectedCheckBox([]);
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultFilterValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency) {
        andConditions.push({
          agency: {
            id: {
              $eq: filterValue.agency.value,
            },
          },
        });
      }
      if (filterValue?.region) {
        andConditions.push({
          mda: {
            region: {
              id: {
                $eq: filterValue.region.value,
              },
            },
          },
        });
      }
      if (filterValue?.mda) {
        andConditions.push({
          mda: {
            id: {
              $eq: filterValue.mda.value,
            },
          },
        });
      }
      if (filterValue?.paymentCategory) {
        andConditions.push({
          paymentCategory: {
            id: {
              $eq: filterValue.paymentCategory.value,
            },
          },
        });
      }
      if (filterValue?.thematicArea) {
        andConditions.push({
          thematicArea: {
            id: {
              $eq: filterValue.thematicArea.value,
            },
          },
        });
      }
      if (filterValue?.sector) {
        andConditions.push({
          sector: {
            id: {
              $eq: filterValue.sector.value,
            },
          },
        });
      }
      if (filterValue?.positionType) {
        andConditions.push({
          positionType: {
            id: {
              $eq: filterValue.positionType.value,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        andConditions.push({
          $or: [
            {
              startDate: {
                $between: [
                  filterValue.dateRange.fromDate?.toLocaleDateString("en-CA"),
                  filterValue.dateRange.toDate?.toLocaleDateString("en-CA"),
                ],
              },
            },
            {
              endDate: {
                $between: [
                  filterValue.dateRange.fromDate?.toLocaleDateString("en-CA"),
                  filterValue.dateRange.toDate?.toLocaleDateString("en-CA"),
                ],
              },
            },
          ],
        });
      }

      if (filterValue?.status) {
        if (filterValue.status === "active")
          andConditions.push({
            startDate: {
              $lte: new Date().toISOString(),
            },
            endDate: {
              $gte: new Date().toISOString(),
            },
          });
        else if (filterValue.status === "inactive")
          andConditions.push({
            $or: [
              {
                startDate: {
                  $gt: new Date().toISOString(),
                },
              },
              {
                endDate: {
                  $lt: new Date().toISOString(),
                },
              },
            ],
          });
      }
      if (filterValue?.projectOrFund) {
        andConditions.push({
          projectOrFund: {
            $containsi: filterValue.projectOrFund,
          },
        });
      }

      andConditions.push({
        $or: [
          {
            individual: {
              fullName: {
                $containsi: searchValue,
              },
            },
          },
          {
            positionTitle: {
              $containsi: searchValue,
            },
          },
          {
            mda: {
              name: {
                $containsi: searchValue,
              },
            },
          },
          {
            mda: {
              region: {
                name: {
                  $containsi: searchValue,
                },
              },
            },
          },
          {
            projectOrFund: {
              $containsi: searchValue,
            },
          }
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const APIParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "agency",
            "mda",
            "mda.region",
            "individual",
            "positionType",
            "paymentCategory",
            "thematicArea",
            "sector",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(APIParams);
        const response: any = await getPayments(APIParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deletePayment(selectedRecord.id);
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Payment deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting payment");
      } finally {
        setDeleting(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deletePayment(id);
          })
        );
        setDeleting(false);
        toast.success("Payments deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "payment",
        action: "delete",
      },
    ]),
    updatedAt: isAllowed(userProfile?.permissions, [
      {
        resource: "individual",
        action: "update",
      },
    ]),
    createdAt: isAllowed(userProfile?.permissions, [
      {
        resource: "individual",
        action: "create",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Ministry/Department/Agency (MDA)",
        accessorKey: "mda.name",
        id: "mda.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  whiteSpace: "pre-wrap",
                  width: "400px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title border bg-primary-subtle text-danger rounded fs-24">
                      <i className="ri-bank-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5
                    className="fs-14 mb-1"
                  // style={{
                  //   whiteSpace: "nowrap",
                  //   overflow: "hidden",
                  //   textOverflow: "ellipsis",
                  // }}
                  >
                    <Permission
                      alternative={
                        <Link
                          to={`/payments/${rowData?.id}`}
                          className="text-body"
                        >
                          {cell.getValue()}
                          {rowData?.mda?.acronym
                            ? ` (${rowData?.mda?.acronym})`
                            : ""}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link
                        to={`/payments/${rowData.id}`}
                        className="text-body"
                      >
                        {cell.getValue()}
                        {rowData?.mda?.acronym
                          ? ` (${rowData?.mda?.acronym})`
                          : ""}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.mda?.region?.name || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },

      {
        header: "Position Title",
        accessorKey: "positionTitle",
        enableColumnFilter: false,
        id: "positionTitle",
        cell: (cell: any) => (
          <TextWrapper value={cell.getValue() || "Not Provided"} />
        ),
      },

      {
        header: "Position Type",
        accessorKey: "positionType.name",
        enableColumnFilter: false,
        id: "positionType.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Payment Category",
        accessorKey: "paymentCategory.name",
        enableColumnFilter: false,
        id: "paymentCategory.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const status = isPaymentActive(
            cell.row.original.startDate || "",
            cell.row.original.endDate || ""
          );
          return (
            <React.Fragment>
              {status === null ? (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  Not Provided
                </span>
              ) : status ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  Active
                </span>
              ) : status === false ? (
                <span className="badge bg-danger-subtle text-danger fs-11">
                  Not Active
                </span>
              ) : null}
            </React.Fragment>
          );
        },
      },
      {
        header: "Amount / Month",
        accessorKey: "amountPerMonth",
        enableColumnFilter: false,
        id: "amountPerMonth",
        cell: (cell: any) => {
          return (
            <>
              {cell.getValue()
                ? `$ ${cell.getValue().toLocaleString()}`
                : "Not Provided"}
            </>
          );
        },
      },

      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        id: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Created At",
        accessorKey: "createdAt",
        id: "createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="payment" action="findOne">
                  <DropdownItem
                    href={`/payments/${rowData.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/payments/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="payment" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm({
                        id: rowData.id,
                        ...rowData,
                      });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="payment" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Payments"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <Filters
            setSearchValue={setSearchValue}
            searchValue={searchValue}
            setFilterValue={setFilterValue}
            filterValue={filterValue}
            setPage={setPage}
            setSorted={setSorted}
            defaultFilterValue={defaultFilterValue}
          />

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <h5 className="card-title mb-1">Payments</h5>
                  <p className="mb-0 text-muted">
                    A list of payments and contracts made to ministries,
                    departments and agencies.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <div className="form-check form-switch form-switch-lg d-flex px-0 form-check-right align-items-center">
                      <Label
                        className="form-check-label me-3"
                        htmlFor="flexSwitchCheckCheckedDisabled2"
                        style={{ width: "120px", marginTop: "5px" }}
                      >
                        Active Payments
                      </Label>{" "}
                      <Input
                        name="isActive"
                        className="form-check-input me-2"
                        type="checkbox"
                        role="switch"
                        id="flexSwitchCheckCheckedDisabled2"
                        checked={filterValue.status === "active" ? true : false}
                        onChange={(e: any) => {
                          setFilterValue({
                            ...filterValue,
                            status: e.target.checked ? "active" : "",
                          });
                          setPage(1);
                        }}
                      />
                    </div>
                    <Permission resource="payment" action="create">
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          showAddModalForm();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Payment
                      </Link>
                    </Permission>

                    <Permissions
                      checks={[
                        {
                          resource: "payment",
                          action: "import",
                        },
                        {
                          resource: "payment",
                          action: "exportToCsv",
                        },
                        {
                          resource: "payment",
                          action: "importUpdate",
                        },
                        {
                          resource: "payment",
                          action: "clearAllData",
                        },
                      ]}
                    >
                      <Import apiParams={apiParams} fetchData={fetchUpdatedData} />
                    </Permissions>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => { }}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap "
              />
              <AddPayment
                toggle={() => setIsAddModalOpen((state) => !state)}
                isModalOpen={isAddModalOpen}
                fetchData={fetchData}
              />
              <UpdatePayment
                toggle={() => setIsUpdateModalOpen((state) => !state)}
                isModalOpen={isUpdateModalOpen}
                selectedRecord={selectedRecord}
                fetchData={fetchUpdatedData}
              />
              <DeleteModal
                show={deleteModal}
                onDeleteClick={() => handleDelete()}
                onCloseClick={() => setDeleteModal(false)}
                loading={deleting}
              />
              <DeleteModal
                show={deleteMultipleModal}
                onDeleteClick={() => handleDeleteMultiple()}
                onCloseClick={() => setDeleteMultipleModal(false)}
                loading={deleting}
              />
            </CardBody>
          </Card>
        </Container>

        <Permissions checks={[{ resource: "payment", action: "delete" }]}>
          <BulkActionBar
            count={selectedCheckBox.length}
            onClear={unCheckAll}
            itemLabel="payment"
          >
            <Button
              color="danger"
              size="sm"
              className="material-shadow-none"
              onClick={() => setDeleteMultipleModal(true)}
            >
              <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
            </Button>
          </BulkActionBar>
        </Permissions>
      </div>
    </React.Fragment>
  );
};

export default Payments;
